import React, { useMemo } from 'react';
import {
  Card,
  List,
  Badge,
  Button,
  Space,
  Typography,
  Empty,
  Spin,
  Tooltip,
  Tag,
} from 'antd';
import {
  BellOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  CalendarOutlined,
  FileTextOutlined,
  TeamOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';
import { notification } from 'antd';
import './notification-center.css';

const { Text, Title } = Typography;

/**
 * Notification configuration mapping
 * Maps event types to icons and display settings
 */
const getNotificationConfig = (event) => {
  const configs = {
    // Appointment events
    'appointment.booked': {
      icon: <CalendarOutlined />,
      type: 'info',
      label: 'Appointment',
      category: 'appointments',
    },
    'appointment.updated': {
      icon: <CalendarOutlined />,
      type: 'info',
      label: 'Appointment',
      category: 'appointments',
    },
    'appointment.cancelled': {
      icon: <CalendarOutlined />,
      type: 'warning',
      label: 'Appointment',
      category: 'appointments',
    },
    'appointment.converted': {
      icon: <CheckCircleOutlined />,
      type: 'success',
      label: 'Appointment',
      category: 'appointments',
    },
    // PA events
    'pa.created': {
      icon: <FileTextOutlined />,
      type: 'info',
      label: 'PA Request',
      category: 'pa',
    },
    'pa.approved': {
      icon: <CheckCircleOutlined />,
      type: 'success',
      label: 'PA Approved',
      category: 'pa',
    },
    'pa.rejected': {
      icon: <ExclamationCircleOutlined />,
      type: 'error',
      label: 'PA Rejected',
      category: 'pa',
    },
    'pa.flagged': {
      icon: <ExclamationCircleOutlined />,
      type: 'warning',
      label: 'PA Flagged',
      category: 'pa',
    },
    // Claim events
    'claim.submitted': {
      icon: <FileTextOutlined />,
      type: 'info',
      label: 'Claim Submitted',
      category: 'claims',
    },
    'claim.paid': {
      icon: <CheckCircleOutlined />,
      type: 'success',
      label: 'Claim Paid',
      category: 'claims',
    },
    'claim.soft_deleted': {
      icon: <ExclamationCircleOutlined />,
      type: 'warning',
      label: 'Claim Deleted',
      category: 'claims',
    },
    'claims.pending_review': {
      icon: <TeamOutlined />,
      type: 'warning',
      label: 'Pending Claims',
      category: 'claims',
    },
    // Spec §10 — a facility resolved every claimable item for an encounter month
    // (submitted or removed). Sent once per facility/month. Further claims and
    // system notices are added here and in config/adminNotificationRoutes.js.
    'claim.month_submitted': {
      icon: <CheckCircleOutlined />,
      type: 'success',
      label: 'Monthly Submission Complete',
      category: 'claims',
    },
    // System events
    'system.alert': {
      icon: <ExclamationCircleOutlined />,
      type: 'warning',
      label: 'System Alert',
      category: 'system',
    },
    'system.maintenance': {
      icon: <ExclamationCircleOutlined />,
      type: 'info',
      label: 'Maintenance',
      category: 'system',
    },
    'system.broadcast': {
      icon: <BellOutlined />,
      type: 'info',
      label: 'Broadcast',
      category: 'system',
    },
    'system.app_update': {
      icon: <BellOutlined />,
      type: 'info',
      label: 'App Update',
      category: 'system',
    },
    'system.emergency': {
      icon: <ExclamationCircleOutlined />,
      type: 'error',
      label: 'Emergency',
      category: 'system',
    },
  };

  return configs[event] || {
    icon: <BellOutlined />,
    type: 'info',
    label: 'Notification',
    category: 'system',
  };
};

/**
 * Format timestamp to relative time (e.g., "5m ago", "2h ago")
 */
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Just now';

  const date = new Date(timestamp);
  const now = new Date();
  const diff = now - date;

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

/**
 * Notification item component
 */
const NotificationItem = ({ notification, onClick }) => {
  const config = getNotificationConfig(notification.event);

  return (
    <List.Item
      className={`notification-item ${!notification.read ? 'unread' : ''}`}
      onClick={onClick}
    >
      <List.Item.Meta
        avatar={
          <div className={`notification-icon ${config.type}`}>
            {config.icon}
          </div>
        }
        title={
          <Space direction="vertical" size={0}>
            <Text strong={!notification.read}>{notification.title}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {formatTimestamp(notification.created_at)}
            </Text>
          </Space>
        }
        description={
          <Text type="secondary" style={{ fontSize: '14px' }}>
            {notification.body}
          </Text>
        }
      />
      {!notification.read && <div className="unread-indicator" />}
    </List.Item>
  );
};

/**
 * AdminNotificationCenter - Main notification center component
 * Displays and manages admin notifications with filtering and pagination
 */
const AdminNotificationCenter = () => {
  const navigate = useNavigate();
  
  const {
    notifications,
    loading,
    unreadCount,
    filter,
    pagination,
    handleFilterChange,
    handlePageChange,
    markAsRead,
    markAllAsRead,
    refresh,
  } = useNotifications({
    autoRefresh: true,
    refreshInterval: 30000, // 30 seconds
    pageSize: 50,
  });

  /**
   * Handle notification click
   * Mark as read and navigate to related resource
   */
  const handleNotificationClick = async (notificationItem) => {
    // Mark as read
    if (!notificationItem.read) {
      await markAsRead(notificationItem.id);
    }

    // Navigate based on event type and payload
    const { event, payload } = notificationItem;

    switch (event) {
      case 'appointment.booked':
      case 'appointment.updated':
      case 'appointment.cancelled':
        if (payload?.appointment_id) {
          navigate(`/admin/appointments`);
        }
        break;

      case 'appointment.converted':
        if (payload?.checkin_code) {
          navigate(`/checkins?code=${encodeURIComponent(payload.checkin_code)}`);
        }
        break;

      case 'pa.created':
      case 'pa.approved':
      case 'pa.rejected':
      case 'pa.flagged':
        if (payload?.checkin_id) {
          navigate(`/preauth`);
          // Could also navigate to specific PA detail
          notification.info({
            message: 'PA Request',
            description: 'PA request is in the Pre-Authorization list',
            duration: 3,
          });
        }
        break;

      case 'claim.submitted':
      case 'claim.paid':
        if (payload?.claim_id) {
          navigate('/claims');
          notification.info({
            message: 'Claim',
            description: 'Claim is in the Claims list',
            duration: 3,
          });
        }
        break;

      case 'claims.pending_review':
        navigate('/admin/pending-claims');
        break;

      default:
        console.log('Unknown notification event:', event);
        break;
    }
  };

  /**
   * Filter tabs configuration
   */
  const filterTabs = [
    { key: 'all', label: 'All' },
    { key: 'appointments', label: 'Appointments' },
    { key: 'pa', label: 'PA Requests' },
    { key: 'claims', label: 'Claims' },
    { key: 'system', label: 'System' },
  ];

  /**
   * Calculate filtered notifications
   * Note: This is a client-side filter for development.
   * In production, the API should handle filtering.
   */
  const filteredNotifications = useMemo(() => {
    if (filter === 'all') return notifications;

    return notifications.filter((n) => {
      const config = getNotificationConfig(n.event);
      return config.category === filter;
    });
  }, [notifications, filter]);

  return (
    <div className="notification-center">
      <Card>
        {/* Header */}
        <div className="notification-center-header">
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <BellOutlined style={{ marginRight: '8px' }} />
              Notifications
            </Title>
            <Text type="secondary">
              Manage your notifications and alerts
            </Text>
          </div>
          <Space>
            <Badge count={unreadCount} offset={[10, 0]}>
              <Button icon={<BellOutlined />}>
                {unreadCount} Unread
              </Button>
            </Badge>
            {unreadCount > 0 && (
              <Button onClick={markAllAsRead}>
                Mark All as Read
              </Button>
            )}
            <Tooltip title="Refresh notifications">
              <Button
                icon={<ReloadOutlined />}
                onClick={refresh}
                loading={loading}
              />
            </Tooltip>
          </Space>
        </div>

        {/* Filter Tabs */}
        <div className="notification-filters">
          <Space>
            {filterTabs.map((tab) => (
              <Button
                key={tab.key}
                type={filter === tab.key ? 'primary' : 'default'}
                onClick={() => handleFilterChange(tab.key)}
              >
                {tab.label}
                {tab.key === 'all' && ` (${notifications.length})`}
                {tab.key !== 'all' && (
                  <Badge
                    count={
                      notifications.filter((n) => {
                        const config = getNotificationConfig(n.event);
                        return config.category === tab.key;
                      }).length
                    }
                    style={{ marginLeft: '4px' }}
                    showZero
                  />
                )}
              </Button>
            ))}
          </Space>
        </div>

        {/* Notifications List */}
        <div className="notification-list-container">
          <Spin spinning={loading}>
            {filteredNotifications.length === 0 ? (
              <Empty
                description="No notifications found"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <List
                className="notification-list"
                dataSource={filteredNotifications}
                renderItem={(item) => (
                  <NotificationItem
                    key={item.id}
                    notification={item}
                    onClick={() => handleNotificationClick(item)}
                  />
                )}
                pagination={
                  pagination.total > pagination.pageSize
                    ? {
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        onChange: handlePageChange,
                        showSizeChanger: false,
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} of ${total}`,
                      }
                    : false
                }
              />
            )}
          </Spin>
        </div>
      </Card>
    </div>
  );
};

export default AdminNotificationCenter;



